import { Clip, Reel } from '@/types/pulsedb';
import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';

export const getReelsForInterviewPaginated = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number,
    starred: boolean | null = null,
    page: number = 1,
    pageSize: number = 25
  ): Promise<{ data: Reel[]; total: number }> => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('reels')
      .select('*', { count: 'exact' })
      .eq('interview_id', interviewId)
      .eq('deleted', false)
      .eq('hidden', false)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (starred !== null) {
      query = query.eq('starred', starred);
    }

    const { data, count } = await query.throwOnError();
    return { data: data || [], total: count || 0 };
  }
);

export const getReelWithClipsBySlug = cache(
  async (supabase: SupabaseClient, slug: string) => {
    const { data: reel, error: reelError } = await supabase
      .from('reels')
      .select('*, reel_clips(index, clips(*))')
      .eq('slug', slug)
      .eq('deleted', false)
      .order('index', { ascending: true, referencedTable: 'reel_clips' })
      .single();
    if (reelError || !reel) {
      return null;
    }

    const reelClips = (reel as any).reel_clips || [];
    const { reel_clips: _, ...reelData } = reel as any;

    const seen = new Set<number>();
    const orderedClips: Clip[] = [];
    for (const rc of reelClips) {
      const clip = rc.clips;
      if (!clip || clip.deleted || seen.has(clip.id)) continue;
      seen.add(clip.id);
      orderedClips.push({ ...clip, index: rc.index });
    }

    return { reel: reelData, clips: orderedClips };
  }
);

export const getReelWithClips = cache(
  async (supabase: SupabaseClient, reelId: number) => {
    const { data: reel, error: reelError } = await supabase
      .from('reels')
      .select('*, reel_clips(index, clips(*))')
      .eq('id', reelId)
      .eq('deleted', false)
      .order('index', { ascending: true, referencedTable: 'reel_clips' })
      .single();
    if (reelError || !reel) {
      return null;
    }

    const reelClips = (reel as any).reel_clips || [];
    const { reel_clips: _, ...reelData } = reel as any;

    // Deduplicate by clip_id preserving index order, filter deleted clips
    const seen = new Set<number>();
    const orderedClips: Clip[] = [];
    for (const rc of reelClips) {
      const clip = rc.clips;
      if (!clip || clip.deleted || seen.has(clip.id)) continue;
      seen.add(clip.id);
      orderedClips.push({ ...clip, index: rc.index });
    }

    return { reel: reelData, clips: orderedClips };
  }
);

export const updateReel = async (
  supabase: SupabaseClient,
  reelId: number,
  data: Partial<{ name: string; description: string | null; starred: boolean }>
) => {
  const { data: reel } = await supabase
    .from('reels')
    .update(data)
    .eq('id', reelId)
    .select()
    .single()
    .throwOnError();
  return reel;
};

export const deleteReel = async (supabase: SupabaseClient, id: number) => {
  await supabase
    .from('reels')
    .update({ deleted: true })
    .eq('id', id)
    .throwOnError();
};

/**
 * Insert a reel and its ordered `reel_clips` rows. `clipIds` are linked in the
 * given order (`index` 0..n). Returns the new reel row.
 */
export const createReel = async (
  supabase: SupabaseClient,
  args: {
    interviewId: number;
    name: string;
    description: string;
    clipIds: number[];
  }
): Promise<Reel> => {
  const { data: reel } = await supabase
    .from('reels')
    .insert({
      interview_id: args.interviewId,
      name: args.name,
      description: args.description
    })
    .select('*')
    .single()
    .throwOnError();

  if (args.clipIds.length > 0) {
    await supabase
      .from('reel_clips')
      .insert(
        args.clipIds.map((clipId, index) => ({
          reel_id: reel.id,
          clip_id: clipId,
          index
        }))
      )
      .throwOnError();
  }
  return reel as unknown as Reel;
};

/**
 * Apply `reelArgs` to a reel and `clipArgs` to all of its clips (e.g. hide a
 * previous report's reel and its clips together).
 */
export const updateReelAndClips = async (
  supabase: SupabaseClient,
  reelId: number,
  reelArgs: Partial<Reel>,
  clipArgs: Partial<Clip>
): Promise<void> => {
  await supabase.from('reels').update(reelArgs).eq('id', reelId).throwOnError();

  const { data: links } = await supabase
    .from('reel_clips')
    .select('clip_id')
    .eq('reel_id', reelId)
    .throwOnError();

  const clipIds = (links ?? []).map((l) => l.clip_id);
  if (clipIds.length > 0) {
    await supabase
      .from('clips')
      .update(clipArgs)
      .in('id', clipIds)
      .throwOnError();
  }
};
